import { OTLayout } from "@/components/layout/ot-layout";
import { useState } from "react";
import { useTablist } from "@/hooks/use-tablist";

type TabId = "deformity" | "grip" | "splinting";

const DEFORMITIES = [
  {
    name: "Ulnar Drift",
    aka: "Ulnar deviation of MCPs",
    conditions: ["RA", "Psoriatic Arthritis"],
    description: "Lateral deviation of the fingers at the MCP joints towards the ulnar side, caused by weakening of the radial collateral ligaments and extensor tendon subluxation.",
    clinicalFeatures: ["MCP joint subluxation", "Extensor tendon subluxation ulnarward", "Reduced grip and pinch strength", "Cosmetic deformity affecting function"],
    otInterventions: ["Working splints to maintain alignment during activity", "Joint protection education — avoid ulnar-deviating forces", "Silver Ring Splints for MCP alignment", "Activity modification and assistive technology"],
  },
  {
    name: "Swan Neck Deformity",
    aka: "Hyperextension of PIP with flexion of DIP",
    conditions: ["RA", "SLE"],
    description: "Hyperextension at the PIP joint with compensatory flexion at the DIP joint. Results from FDS weakening, intrinsic tightening, or volar plate attenuation.",
    clinicalFeatures: ["PIP hyperextension", "DIP flexion", "Loss of initial PIP flexion", "Reduced functional grip"],
    otInterventions: ["Silver Ring Splints (Oval-8 or custom) to block PIP hyperextension", "Intrinsic stretching exercises", "Tendon gliding exercises", "Activity modification for grip tasks"],
  },
  {
    name: "Boutonnière Deformity",
    aka: "Central slip rupture pattern",
    conditions: ["RA", "Psoriatic Arthritis"],
    description: "PIP flexion with DIP hyperextension, caused by central slip rupture allowing lateral bands to migrate volarly.",
    clinicalFeatures: ["PIP joint flexion contracture", "DIP hyperextension", "Reduced PIP extension", "Elson's test positive in acute presentation"],
    otInterventions: ["PIP extension splinting (avoid DIP splinting in isolation)", "Gentle DIP flexion exercises to restore lateral band position", "Referral to hand surgeon for acute central slip rupture", "Dynamic splinting for flexible deformities"],
  },
  {
    name: "Z-Thumb (Hitchhiker's Thumb)",
    aka: "Zigzag deformity of thumb",
    conditions: ["RA"],
    description: "IP hyperextension with MCP flexion (Type I) or IP flexion with MCP instability and CMC subluxation (Type III). Severely impairs pinch function.",
    clinicalFeatures: ["Zigzag appearance of thumb", "IP joint hyperextension (Type I)", "CMC subluxation", "Reduced pinch strength and lateral pinch"],
    otInterventions: ["Thumb post / CMC stabilising splint", "IP extension blocking splint for Type I", "Joint protection for CMC joint", "Adapted grip technique teaching"],
  },
  {
    name: "Mallet Finger",
    aka: "Terminal extensor tendon rupture",
    conditions: ["RA", "Trauma"],
    description: "Rupture or attenuation of the terminal extensor tendon at the DIP joint, resulting in a flexion deformity.",
    clinicalFeatures: ["DIP flexion deformity", "Inability to actively extend DIP", "May have associated avulsion fracture", "Pain at DIP dorsum in acute phase"],
    otInterventions: ["DIP extension splint continuously for 6–8 weeks (Stack splint or custom)", "Night splinting for further 6 weeks post-immobilisation", "Monitor for pressure areas under splint", "Active DIP flexion not permitted during splinting phase"],
  },
];

const GRIP_TESTS = [
  {
    test: "Jamar Dynamometer — Grip Strength",
    description: "Gold standard for grip strength assessment in rheumatology. Standardised position: seated, shoulder adducted, elbow at 90°, forearm neutral, wrist 0–30° extension.",
    protocol: ["Three trials each hand", "Record mean of three attempts", "Compare dominant vs non-dominant", "Compare to normative data by age and sex"],
    norms: [
      { group: "Women 20–29", dominant: "33–38 kg", nonDominant: "28–34 kg" },
      { group: "Women 30–39", dominant: "35–40 kg", nonDominant: "30–36 kg" },
      { group: "Women 50–59", dominant: "27–32 kg", nonDominant: "23–28 kg" },
      { group: "Women 60–69", dominant: "22–28 kg", nonDominant: "19–25 kg" },
      { group: "Men 20–29", dominant: "52–60 kg", nonDominant: "46–54 kg" },
      { group: "Men 30–39", dominant: "54–62 kg", nonDominant: "48–56 kg" },
      { group: "Men 50–59", dominant: "44–52 kg", nonDominant: "39–47 kg" },
      { group: "Men 60–69", dominant: "37–45 kg", nonDominant: "32–40 kg" },
    ],
    mcid: "Approximately 5 kg is considered a clinically meaningful change in RA populations (Gordon et al., Arthritis Care Res).",
    notes: "Avoid during active flare. Consider pain score before and after. Grip strength is a predictor of functional decline in RA.",
  },
  {
    test: "Pinch Strength",
    description: "Lateral (key) pinch and tip pinch assessed with a pinchmeter. Particularly relevant in thumb-deforming conditions and carpal tunnel.",
    protocol: ["Lateral pinch: thumb pulp to radial side of index finger", "Tip pinch: tip of thumb to tip of index finger", "Three trials, record mean"],
    norms: [
      { group: "Women 20–29", dominant: "5.5–7.5 kg", nonDominant: "5.0–7.0 kg" },
      { group: "Women 50–59", dominant: "4.5–6.5 kg", nonDominant: "4.0–6.0 kg" },
      { group: "Men 20–29", dominant: "8.0–11.0 kg", nonDominant: "7.5–10.0 kg" },
      { group: "Men 50–59", dominant: "7.0–9.5 kg", nonDominant: "6.5–9.0 kg" },
    ],
    mcid: "Not well-established; use clinical context.",
    notes: "Reduced in carpal tunnel syndrome (CTD), ulnar nerve involvement, CMC OA, Z-thumb deformity.",
  },
  {
    test: "Moberg Pick-Up Test",
    description: "Functional sensory test assessing tactile gnosis. Patient picks up 12 standardised objects with vision and without vision.",
    protocol: ["Place 12 objects on table (coins, nuts, bolts, safety pin)", "Patient picks up objects one at a time into a small box", "Timed: with vision, then without vision", "Record time and observe patterns of pick-up"],
    norms: [{ group: "Normal adults", dominant: "18–22 seconds (blind)", nonDominant: "20–25 seconds (blind)" }],
    mcid: "Qualitative assessment — note which fingers used and prehension patterns.",
    notes: "Useful to detect median nerve compromise in RA carpal tunnel. Also relevant in SSc with digital ischaemia.",
  },
];

const SPLINTING_PROTOCOLS = [
  {
    indication: "RA Flare / Active Synovitis",
    splintType: "Resting Hand Splint (Volar or Bivalved)",
    material: "Low-temperature thermoplastic (e.g. Aquaplast 3.2 mm, Orfit Classic)",
    position: "Wrist 0–15° extension, MCPs 30–70° flexion, IPs near full extension, thumb abducted in opposition",
    wearingSchedule: "Nights and rest periods; remove for hygiene and gentle exercise",
    reviewCriteria: ["Pain reduction at 2 weeks", "Assess for pressure areas at 48h", "Reassess fit as oedema resolves", "Wean during remission"],
    notes: "Padded edges essential over MCP prominences. Check circulation and sensation after fitting. Avoid during concurrent infection.",
  },
  {
    indication: "Swan Neck Deformity — Flexible",
    splintType: "Silver Ring Splint (Oval-8 or Custom)",
    material: "Sterling silver (custom) or thermoplastic (interim: Oval-8 / Stack splint)",
    position: "PIP joint blocked from full extension; allows flexion",
    wearingSchedule: "During functional activities; remove at night unless hyperextension occurs during sleep",
    reviewCriteria: ["Check PIP ROM at 4 weeks", "Verify adequate blocking without restricting flexion", "Patient satisfaction with cosmesis and function"],
    notes: "Silver Ring Splints have good evidence in flexible swan neck deformity (Zijlstra et al.). Refer to specialist fabricator for custom silver splints.",
  },
  {
    indication: "Wrist Support — CTD / RA Wrist",
    splintType: "Wrist Working Splint",
    material: "Neoprene (off-the-shelf) or low-temperature thermoplastic for custom",
    position: "Wrist 0–15° extension; fingers and thumb free",
    wearingSchedule: "During prolonged activities; remove for grip and pinch tasks where wrist motion is needed",
    reviewCriteria: ["Pain and function at 4 weeks", "Assess for thenar or hypothenar pressure", "Review if skin breakdown or oedema worsens"],
    notes: "Custom thermoplastic wrist splints provide better positioning than neoprene but are less tolerated long-term. Consider combined approach.",
  },
  {
    indication: "Boutonnière Deformity — Early",
    splintType: "PIP Extension Splint",
    material: "Low-temperature thermoplastic (2.0 mm Aquaplast) or Oval-8",
    position: "PIP in full extension; DIP and MCP free",
    wearingSchedule: "Continuous for 6–8 weeks in early/acute presentation; night splinting ongoing for maintenance",
    reviewCriteria: ["Monitor PIP extension AROM/PROM weekly", "Assess for skin breakdown", "Wean when extension maintained actively"],
    notes: "DIP must remain free to allow active DIP flexion which helps restore lateral band position. Do not immobilise DIP.",
  },
  {
    indication: "CMC Thumb OA / Z-Thumb",
    splintType: "Thumb Post / Thumb CMC Stabilising Splint",
    material: "Low-temperature thermoplastic (2.0 mm) or neoprene thumb splint",
    position: "CMC joint stabilised in palmar abduction; MCP and IP joints free",
    wearingSchedule: "During functional activities — especially pinch and grip tasks; remove for fine motor tasks",
    reviewCriteria: ["Pain and function at 4 weeks", "Assess for 1st web space pressure", "Review grip strength"],
    notes: "Avoid immobilising MCP and IP — reduces functional use. Bennett et al. (2013) showed splinting reduces pain in CMC OA.",
  },
];

const HAND_TABS: { id: TabId; label: string }[] = [
  { id: "deformity", label: "Deformity Guide" },
  { id: "grip", label: "Grip Assessment" },
  { id: "splinting", label: "Splinting Protocols" },
];

export default function HandAssessment() {
  const [tab, setTab] = useState<TabId>("deformity");
  const [openDeformity, setOpenDeformity] = useState<string | null>(null);
  const { getTabProps } = useTablist<TabId>({ items: HAND_TABS, activeId: tab, onActivate: setTab });

  return (
    <OTLayout title="Hand Assessment & Splinting">
      <div className="space-y-6">

        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 text-sm text-amber-800">
          <strong>Clinical reference only.</strong> All assessments and splinting must be based on individual patient assessment. Check for contraindications (active infection, circulatory compromise, skin fragility) before fitting any splint.
        </div>

        {/* Tabs */}
        <div role="tablist" aria-label="Hand assessment sections" className="flex gap-1 bg-slate-100 rounded-xl p-1">
          {HAND_TABS.map((t, i) => (
            <button
              key={t.id}
              {...getTabProps(t.id, i)}
              aria-controls={`hand-panel-${t.id}`}
              id={`hand-tab-${t.id}`}
              className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${tab === t.id ? "bg-white shadow-sm text-amber-700" : "text-slate-500 hover:text-slate-700"}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Deformity Guide */}
        {tab === "deformity" && (
          <div id="hand-panel-deformity" role="tabpanel" aria-labelledby="hand-tab-deformity" className="space-y-4">
            <h2 className="text-lg font-bold text-slate-900">Rheumatological Hand Deformities</h2>
            {DEFORMITIES.map((d) => (
              <div key={d.name} className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                <button
                  className="w-full px-6 py-4 text-left flex items-center justify-between"
                  onClick={() => setOpenDeformity(openDeformity === d.name ? null : d.name)}
                >
                  <div>
                    <h3 className="font-bold text-slate-900">{d.name}</h3>
                    <p className="text-sm text-slate-500">{d.aka}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {d.conditions.map((c) => (
                      <span key={c} className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">{c}</span>
                    ))}
                    <span className="text-slate-400 ml-2">{openDeformity === d.name ? "▲" : "▼"}</span>
                  </div>
                </button>
                {openDeformity === d.name && (
                  <div className="px-6 pb-6 space-y-4 border-t border-slate-100">
                    <p className="text-sm text-slate-700 mt-4">{d.description}</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Clinical Features</h4>
                        <ul className="space-y-1">
                          {d.clinicalFeatures.map((f) => (
                            <li key={f} className="text-sm text-slate-700 flex items-start gap-2">
                              <span className="text-amber-500 mt-1">•</span>{f}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">OT Interventions</h4>
                        <ul className="space-y-1">
                          {d.otInterventions.map((i) => (
                            <li key={i} className="text-sm text-slate-700 flex items-start gap-2">
                              <span className="text-amber-500 mt-1">•</span>{i}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Grip Assessment */}
        {tab === "grip" && (
          <div id="hand-panel-grip" role="tabpanel" aria-labelledby="hand-tab-grip" className="space-y-6">
            <h2 className="text-lg font-bold text-slate-900">Grip & Pinch Assessment Tools</h2>
            {GRIP_TESTS.map((g) => (
              <div key={g.test} className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4">
                <h3 className="font-bold text-slate-900">{g.test}</h3>
                <p className="text-sm text-slate-600">{g.description}</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Protocol</h4>
                    <ol className="space-y-1">
                      {g.protocol.map((p, i) => (
                        <li key={i} className="text-sm text-slate-700 flex gap-2">
                          <span className="text-amber-600 font-bold shrink-0">{i + 1}.</span>{p}
                        </li>
                      ))}
                    </ol>
                  </div>
                  <div>
                    <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Normative Values</h4>
                    <div className="space-y-2">
                      {g.norms.map((n) => (
                        <div key={n.group} className="bg-slate-50 rounded-lg px-3 py-2">
                          <p className="text-xs font-medium text-slate-700">{n.group}</p>
                          <p className="text-xs text-slate-500">Dom: {n.dominant} | Non-dom: {n.nonDominant}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="bg-amber-50 border border-amber-100 rounded-xl p-3">
                  <p className="text-xs font-semibold text-amber-700 mb-1">MCID</p>
                  <p className="text-sm text-amber-800">{g.mcid}</p>
                </div>
                <p className="text-xs text-slate-500 italic">{g.notes}</p>
              </div>
            ))}
          </div>
        )}

        {/* Splinting Protocols */}
        {tab === "splinting" && (
          <div id="hand-panel-splinting" role="tabpanel" aria-labelledby="hand-tab-splinting" className="space-y-4">
            <h2 className="text-lg font-bold text-slate-900">Splinting Protocols by Indication</h2>
            {SPLINTING_PROTOCOLS.map((s) => (
              <div key={s.indication} className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="font-bold text-slate-900">{s.indication}</h3>
                    <p className="text-sm text-amber-700 font-medium mt-0.5">{s.splintType}</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Material</h4>
                    <p className="text-sm text-slate-700">{s.material}</p>
                  </div>
                  <div>
                    <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Position</h4>
                    <p className="text-sm text-slate-700">{s.position}</p>
                  </div>
                  <div>
                    <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Wearing Schedule</h4>
                    <p className="text-sm text-slate-700">{s.wearingSchedule}</p>
                  </div>
                </div>
                <div>
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Review Criteria</h4>
                  <ul className="flex flex-wrap gap-2">
                    {s.reviewCriteria.map((c) => (
                      <li key={c} className="text-xs bg-slate-100 text-slate-700 px-2 py-1 rounded-full">{c}</li>
                    ))}
                  </ul>
                </div>
                <p className="text-xs text-slate-500 italic border-t border-slate-100 pt-3">{s.notes}</p>
              </div>
            ))}
          </div>
        )}

      </div>
    </OTLayout>
  );
}
